import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Mail,
  MessageSquare,
  Phone,
  DollarSign,
  Activity,
  Send,
  Eye,
  MousePointer,
  CheckCircle,
  Clock,
  Play,
  Pause,
  AlertCircle,
  ArrowRight,
  Calendar,
  BarChart3,
  Bot,
  Zap,
  Target
} from 'lucide-react';
import { getDateRange } from '../lib/google-ads-client';

interface DashboardStats {
  campaigns: {
    total: number;
    active: number;
    scheduled: number;
  };
  segments: {
    total: number;
    active: number;
  };
  templates: {
    email: number;
    sms: number;
    aiCalls: number;
  };
  todayMetrics: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
  };
  revenueMetrics: {
    today: number;
    week: number;
    month: number;
  };
}

interface RecentActivity {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  status: string;
}

export default function DashboardPage() {
  const { marketingUser } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    campaigns: { total: 0, active: 0, scheduled: 0 },
    segments: { total: 0, active: 0 },
    templates: { email: 0, sms: 0, aiCalls: 0 },
    todayMetrics: { sent: 0, delivered: 0, opened: 0, clicked: 0 },
    revenueMetrics: { today: 0, week: 0, month: 0 },
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [aiAgentStatus, setAIAgentStatus] = useState<{
    autonomy_mode: string;
    pending_decisions: number;
    today_actions: number;
  } | null>(null);
  const [googleAdsMetrics, setGoogleAdsMetrics] = useState<{
    impressions: number;
    clicks: number;
    cost: number;
    conversions: number;
    ctr: number;
    activeCampaigns: number;
  } | null>(null);

  useEffect(() => {
    loadDashboardData();
    loadAIAgentStatus();
    loadGoogleAdsMetrics();
  }, []);

  const loadAIAgentStatus = async () => {
    try {
      // Load AI agent config
      const { data: config } = await supabase
        .from('ai_agent_config')
        .select('autonomy_mode')
        .single();

      // Load pending decisions count
      const { count: pendingCount } = await supabase
        .from('ai_agent_decisions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // Load today's actions count
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: actionsCount } = await supabase
        .from('ai_agent_actions')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      setAIAgentStatus({
        autonomy_mode: config?.autonomy_mode || 'manual',
        pending_decisions: pendingCount || 0,
        today_actions: actionsCount || 0
      });
    } catch (error) {
      console.error('Error loading AI agent status:', error);
    }
  };

  const loadGoogleAdsMetrics = async () => {
    try {
      const range = getDateRange(30);
      const { data } = await supabase
        .from('google_ads_campaign_performance')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate);

      if (data && data.length > 0) {
        const totals = data.reduce((acc, row) => ({
          impressions: acc.impressions + (row.impressions || 0),
          clicks: acc.clicks + (row.clicks || 0),
          cost: acc.cost + ((row.cost_micros || 0) / 1000000),
          conversions: acc.conversions + (row.conversions || 0)
        }), { impressions: 0, clicks: 0, cost: 0, conversions: 0 });

        const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
        const activeCampaigns = new Set(data.map(row => row.campaign_id)).size;

        setGoogleAdsMetrics({
          ...totals,
          ctr,
          activeCampaigns
        });
      }
    } catch (error) {
      console.error('Error loading Google Ads metrics:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load campaigns data
      const { data: campaigns } = await supabase
        .from('marketing_campaigns')
        .select('status, sent_count, delivered_count, opened_count, clicked_count, created_at');

      // Load segments data
      const { data: segments } = await supabase
        .from('marketing_segments')
        .select('id, is_dynamic');

      // Load templates data
      const { data: emailTemplates } = await supabase
        .from('marketing_email_templates')
        .select('id')
        .eq('is_active', true);

      const { data: smsTemplates } = await supabase
        .from('marketing_sms_templates')
        .select('id')
        .eq('is_active', true);

      const { data: aiCallTemplates } = await supabase
        .from('marketing_ai_call_scripts')
        .select('id')
        .eq('is_active', true);

      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const campaignsToday = campaigns?.filter(c => new Date(c.created_at) >= today) || [];
      const todaySent = campaignsToday.reduce((sum, c) => sum + c.sent_count, 0);
      const todayDelivered = campaignsToday.reduce((sum, c) => sum + c.delivered_count, 0);
      const todayOpened = campaignsToday.reduce((sum, c) => sum + c.opened_count, 0);
      const todayClicked = campaignsToday.reduce((sum, c) => sum + c.clicked_count, 0);

      setStats({
        campaigns: {
          total: campaigns?.length || 0,
          active: campaigns?.filter(c => c.status === 'active').length || 0,
          scheduled: campaigns?.filter(c => c.status === 'scheduled').length || 0,
        },
        segments: {
          total: segments?.length || 0,
          active: segments?.filter(s => s.is_dynamic).length || 0,
        },
        templates: {
          email: emailTemplates?.length || 0,
          sms: smsTemplates?.length || 0,
          aiCalls: aiCallTemplates?.length || 0,
        },
        todayMetrics: {
          sent: todaySent,
          delivered: todayDelivered,
          opened: todayOpened,
          clicked: todayClicked,
        },
        revenueMetrics: {
          today: 0, // Would be calculated from actual revenue data
          week: 0,
          month: 0,
        },
      });

      // Build recent activity from campaigns
      const activities: RecentActivity[] = (campaigns || [])
        .slice(0, 10)
        .map(c => ({
          id: c.created_at,
          type: 'campaign',
          title: c.status === 'active' ? 'Campaign Started' : 'Campaign Created',
          description: `Status: ${c.status}`,
          timestamp: c.created_at,
          status: c.status,
        }));

      setRecentActivity(activities);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="w-4 h-4 text-green-500" />;
      case 'paused': return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-purple-500" />;
      case 'scheduled': return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const calculateOpenRate = () => {
    if (stats.todayMetrics.delivered === 0) return 0;
    return ((stats.todayMetrics.opened / stats.todayMetrics.delivered) * 100).toFixed(1);
  };

  const calculateClickRate = () => {
    if (stats.todayMetrics.opened === 0) return 0;
    return ((stats.todayMetrics.clicked / stats.todayMetrics.opened) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {marketingUser?.first_name}!
        </h1>
        <p className="text-gray-600 mt-2">
          Here's what's happening with your marketing automation today.
        </p>
      </div>

      {/* AI Agent Status - Prominent Feature */}
      {aiAgentStatus && (
        <div className="mb-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <Bot className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">🤖 AI Marketing Agent</h2>
                <p className="text-purple-100 mt-1">
                  {aiAgentStatus.autonomy_mode === 'autonomous' 
                    ? '✅ Fully Autonomous - Operating independently' 
                    : aiAgentStatus.autonomy_mode === 'semi_autonomous'
                    ? '⚡ Semi-Autonomous - Auto-approving high-confidence actions'
                    : '👤 Manual Mode - Awaiting your approval'}
                </p>
              </div>
            </div>
            <Link
              to="/ai-agent"
              className="px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-purple-50 font-semibold transition-colors flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Control Panel
            </Link>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold">{aiAgentStatus.pending_decisions}</div>
              <div className="text-sm text-purple-100 mt-1">Pending Decisions</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold">{aiAgentStatus.today_actions}</div>
              <div className="text-sm text-purple-100 mt-1">Actions Today</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold capitalize">{aiAgentStatus.autonomy_mode.replace('_', ' ')}</div>
              <div className="text-sm text-purple-100 mt-1">Mode</div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Campaigns */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
            <Link to="/campaigns" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View All <ArrowRight className="w-4 h-4 inline" />
            </Link>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.campaigns.total}</h3>
          <p className="text-sm text-gray-600 mt-1">Total Campaigns</p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-green-600">
              <Play className="w-4 h-4" />
              {stats.campaigns.active} active
            </span>
            <span className="flex items-center gap-1 text-blue-600">
              <Clock className="w-4 h-4" />
              {stats.campaigns.scheduled} scheduled
            </span>
          </div>
        </div>

        {/* Segments */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <Link to="/segments" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
              View All <ArrowRight className="w-4 h-4 inline" />
            </Link>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.segments.total}</h3>
          <p className="text-sm text-gray-600 mt-1">Customer Segments</p>
          <div className="mt-4 text-sm text-gray-600">
            {stats.segments.active} active segments
          </div>
        </div>

        {/* Templates */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <Link to="/templates" className="text-sm text-green-600 hover:text-green-700 font-medium">
              View All <ArrowRight className="w-4 h-4 inline" />
            </Link>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">
            {stats.templates.email + stats.templates.sms + stats.templates.aiCalls}
          </h3>
          <p className="text-sm text-gray-600 mt-1">Active Templates</p>
          <div className="mt-4 flex items-center gap-3 text-sm text-gray-600">
            <span>{stats.templates.email} email</span>
            <span>{stats.templates.sms} SMS</span>
            <span>{stats.templates.aiCalls} calls</span>
          </div>
        </div>

        {/* Today's Activity */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Activity className="w-6 h-6 text-amber-600" />
            </div>
            <Link to="/analytics" className="text-sm text-amber-600 hover:text-amber-700 font-medium">
              View Analytics <ArrowRight className="w-4 h-4 inline" />
            </Link>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.todayMetrics.sent.toLocaleString()}</h3>
          <p className="text-sm text-gray-600 mt-1">Messages Sent Today</p>
          <div className="mt-4 text-sm text-gray-600">
            {stats.todayMetrics.opened} opens • {stats.todayMetrics.clicked} clicks
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Today's Performance */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Today's Performance</h2>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Send className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{stats.todayMetrics.sent}</div>
              <div className="text-sm text-gray-600">Sent</div>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{stats.todayMetrics.delivered}</div>
              <div className="text-sm text-gray-600">Delivered</div>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Eye className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{stats.todayMetrics.opened}</div>
              <div className="text-sm text-gray-600">Opened ({calculateOpenRate()}%)</div>
            </div>

            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <MousePointer className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{stats.todayMetrics.clicked}</div>
              <div className="text-sm text-gray-600">Clicked ({calculateClickRate()}%)</div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overall Engagement Rate</p>
                <p className="text-2xl font-bold text-gray-900">{calculateOpenRate()}%</p>
                <p className="text-xs text-gray-500 mt-1">Based on today's metrics</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-gray-600">
                  <Activity className="w-5 h-5" />
                  <span className="text-sm">{stats.todayMetrics.opened} of {stats.todayMetrics.delivered} opened</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/campaigns?action=new"
              className="block w-full p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="font-semibold text-gray-900">New Campaign</div>
                  <div className="text-sm text-gray-600">Launch a new campaign</div>
                </div>
              </div>
            </Link>

            <Link
              to="/segments?action=new"
              className="block w-full p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="font-semibold text-gray-900">Create Segment</div>
                  <div className="text-sm text-gray-600">Define target audience</div>
                </div>
              </div>
            </Link>

            <Link
              to="/templates?action=new"
              className="block w-full p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-green-600" />
                <div>
                  <div className="font-semibold text-gray-900">New Template</div>
                  <div className="text-sm text-gray-600">Design message template</div>
                </div>
              </div>
            </Link>

            <Link
              to="/analytics"
              className="block w-full p-4 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="font-semibold text-gray-900">View Analytics</div>
                  <div className="text-sm text-gray-600">Detailed reports & insights</div>
                </div>
              </div>
            </Link>

            <Link
              to="/ai-agent"
              className="block w-full p-4 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 rounded-lg transition-colors text-left border-2 border-purple-200"
            >
              <div className="flex items-center gap-3">
                <Bot className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="font-semibold text-gray-900">AI Agent Monitor</div>
                  <div className="text-sm text-gray-600">Control autonomous AI</div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
          <Link to="/campaigns" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            View All <ArrowRight className="w-4 h-4 inline" />
          </Link>
        </div>

        {recentActivity.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No recent activity</p>
            <p className="text-sm text-gray-400 mt-1">Create your first campaign to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentActivity.slice(0, 5).map((activity) => (
              <div
                key={activity.id}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-shrink-0">
                  {getStatusIcon(activity.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{activity.title}</p>
                  <p className="text-sm text-gray-600 truncate">{activity.description}</p>
                </div>
                <div className="flex-shrink-0 text-sm text-gray-500">
                  {new Date(activity.timestamp).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Google Ads Performance Section */}
      {googleAdsMetrics && (
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Google Ads Performance</h2>
                <p className="text-sm text-gray-600">Last 30 days</p>
              </div>
            </div>
            <Link
              to="/google-ads"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
            >
              View All Tools
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Eye className="w-6 h-6 text-gray-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsMetrics.impressions.toLocaleString()}</div>
              <div className="text-xs text-gray-600">Impressions</div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <MousePointer className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsMetrics.clicks.toLocaleString()}</div>
              <div className="text-xs text-gray-600">Clicks</div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsMetrics.ctr.toFixed(2)}%</div>
              <div className="text-xs text-gray-600">CTR</div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">${googleAdsMetrics.cost.toFixed(2)}</div>
              <div className="text-xs text-gray-600">Spend</div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsMetrics.conversions}</div>
              <div className="text-xs text-gray-600">Conversions</div>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Send className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsMetrics.activeCampaigns}</div>
              <div className="text-xs text-gray-600">Campaigns</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link
              to="/google-ads/bids"
              className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium text-blue-700 flex items-center justify-between"
            >
              <span>Bid Management</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/google-ads/quality-score"
              className="p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-sm font-medium text-purple-700 flex items-center justify-between"
            >
              <span>Quality Scores</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/google-ads/reporting"
              className="p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-sm font-medium text-green-700 flex items-center justify-between"
            >
              <span>Custom Reports</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* System Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Email Service</h3>
          </div>
          <p className="text-sm text-gray-600">All systems operational</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">SMS Service</h3>
          </div>
          <p className="text-sm text-gray-600">Ready to send messages</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">AI Calls</h3>
          </div>
          <p className="text-sm text-gray-600">Voice system active</p>
        </div>
      </div>
    </div>
  );
}
